import React, { FC, ChangeEvent } from 'react'
import { Controller } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import ControlledTextArea from 'transfix-form/components/ControlledTextArea'
import { Button } from 'transfix-ui/components/Button'
import { TextField } from 'transfix-ui/components/TextField'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { FullPageOverlay } from 'apps/fleet-planner/components/modals/full-page-overlay/FullPageOverlay'
import { useUserContext } from 'apps/fleet-planner/context/UserContext'
import {
  useLaneOfferInterestForm,
  NewInterestSchemaType,
} from 'apps/fleet-planner/hooks/useLaneOfferInterestForm'
import { getInlineMessageProps } from '../modals/CreateLoadOverlay'

interface IDedicatedLanesInquireModalProps {
  isOpen: boolean
  submitHandler: (data: NewInterestSchemaType) => void
  onClose: () => void
  cancelModalHandler?: () => void
  isLoading?: boolean
}

export const DedicatedLanesInquireModal: FC<IDedicatedLanesInquireModalProps> = ({
  cancelModalHandler,
  isLoading,
  isOpen,
  onClose,
  submitHandler,
}) => {
  const { userData } = useUserContext()
  const { user } = userData || {}
  const { email, phone = '' } = user || {}
  const { control, errors: formErrors, handleSubmit } = useLaneOfferInterestForm({
    formDefaultValues: {
      contactEmail: email,
      contactPhone: phone,
    },
  })

  const submitFormHandler = handleSubmit(submitHandler)
  const { isMobile } = useBreakpointContext()
  const inlineMessageProps = getInlineMessageProps(
    !isEmpty(formErrors) ? 'Please fill out the required fields below' : ''
  )
  return (
    <FullPageOverlay
      title='Contact Us'
      isOpen={isOpen}
      onClose={onClose}
      fullScreen={isMobile}
      inlineMessageProps={inlineMessageProps}
      showContentDividers
      maxWidth='md'
      onSubmit={submitFormHandler}
      footer={
        <>
          <Button fullWidth={isMobile} color='tertiary' onClick={cancelModalHandler} size='large'>
            Cancel
          </Button>
          <Button
            data-test='dedicated-lanes-inquire-modal-submit-button'
            color='primary'
            disabled={isLoading}
            fullWidth={isMobile}
            size='large'
            onClick={submitFormHandler}>
            Request Information
          </Button>
        </>
      }>
      <Box p={1}>
        <Typography variant='body1'>
          Our Carrier Sales team will reply as soon as possible.
        </Typography>
        <Box className={formErrors?.contactMessage?.message && 'invalid'} mt={2}>
          <ControlledTextArea
            name='contactMessage'
            defaultValue=''
            control={control}
            TextAreaProps={{
              dataTest: 'dedicated-lanes-inquire-modal-contact-message',
              required: true,
              invalid: !!formErrors?.contactMessage,
              label: 'Your Message',
              placeholder: 'Write something...',
            }}
          />
          {formErrors?.contactMessage?.message && (
            <span className='validation-message'>{formErrors?.contactMessage?.message}</span>
          )}
        </Box>
        <Box mt={3}>
          <Typography variant='subtitle1'>Your contact info</Typography>
        </Box>
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name='contactEmail'
                control={control}
                render={({ onChange, value }) => {
                  const changeHandler = ({
                    target: { value: val = '' },
                  }: ChangeEvent<HTMLInputElement>) => {
                    onChange(val)
                  }
                  return (
                    <TextField
                      required
                      large
                      dataTest='dedicated-lanes-inquire-modal-contact-email'
                      value={value}
                      onChange={changeHandler}
                      label='Contact Email'
                      error={!!formErrors?.contactEmail}
                      validationMessage={formErrors?.contactEmail?.message}
                      placeholder='henryford@example.com'
                      className='fs-mask'
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ControlledPhoneInput
                control={control}
                name='contactPhone'
                PhoneInputProps={{
                  dataTest: 'dedicated-lanes-inquire-modal-contact-phone',
                  required: true,
                  placeholder: '(000) 000-0000',
                  large: true,
                  label: 'Contact Phone',
                  invalid: !!formErrors.contactPhone,
                  validationMessage: formErrors?.contactPhone?.message,
                  className: 'fs-mask',
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </FullPageOverlay>
  )
}
